import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { WorkerAssignment } from '../types/workerAssignment';
import api from '../services/api';

interface ReceiveLine {
  workerId: string;
  assignmentId: number | null;
  qtyReceived: string;
  defectQty: string;
  rate: string;
}

const emptyLine: ReceiveLine = {
  workerId: '',
  assignmentId: null,
  qtyReceived: '',
  defectQty: '',
  rate: '',
};

interface BulkReceiveDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  assignments: WorkerAssignment[];
}

export default function BulkReceiveDialog({ open, onClose, onSaved, assignments }: BulkReceiveDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [lines, setLines] = useState<ReceiveLine[]>([{ ...emptyLine }]);
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [submitResults, setSubmitResults] = useState<{
    successCount: number;
    errors: Array<{ label: string; error: string }>;
  } | null>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setReceiveDate(new Date().toISOString().split('T')[0]);
      setLines([{ ...emptyLine }]);
      setSubmitResults(null);
      setSubmitProgress(0);
    }
  }, [open]);

  // Pending assignments (remaining > 0)
  const pendingAssignments = useMemo(() => {
    return assignments.filter(a => {
      const totalReceived = a.receives.reduce((sum, r) => sum + r.quantity_received + r.defect_quantity, 0);
      return a.quantity_issued - totalReceived > 0;
    });
  }, [assignments]);

  // Unique workers with pending assignments
  const workers = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    pendingAssignments.forEach(a => {
      if (!map.has(a.worker_id)) {
        map.set(a.worker_id, { id: a.worker_id, name: a.worker.name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [pendingAssignments]);

  const getRemaining = (a: WorkerAssignment) => {
    const totalReceived = a.receives.reduce((sum, r) => sum + r.quantity_received + r.defect_quantity, 0);
    return a.quantity_issued - totalReceived;
  };

  const getAssignmentLabel = (a: WorkerAssignment) => {
    const remaining = getRemaining(a);
    return `${a.design_no} · ${a.garment_part} · ${a.size} (Remaining: ${remaining})`;
  };

  const getWorkerPendingAssignments = (workerId: string) => {
    return pendingAssignments.filter(a => a.worker_id === workerId);
  };

  // Line helpers
  const updateLine = (idx: number, updates: Partial<ReceiveLine>) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...updates } : l));
  };

  const addLine = () => setLines(prev => [...prev, { ...emptyLine }]);

  const removeLine = (idx: number) => setLines(prev => prev.filter((_, i) => i !== idx));

  const handleWorkerChange = (idx: number, worker: { id: string; name: string } | null) => {
    updateLine(idx, {
      workerId: worker?.id || '',
      assignmentId: null,
      qtyReceived: '',
      defectQty: '',
      rate: '',
    });
  };

  const handleAssignmentChange = (idx: number, assignment: WorkerAssignment | null) => {
    if (assignment) {
      updateLine(idx, {
        assignmentId: assignment.id,
        rate: assignment.stitching_rate != null ? String(assignment.stitching_rate) : '',
        qtyReceived: '',
        defectQty: '',
      });
    } else {
      updateLine(idx, { assignmentId: null, rate: '', qtyReceived: '', defectQty: '' });
    }
  };

  // Validation
  const getLineError = (line: ReceiveLine): string | null => {
    if (!line.assignmentId) return null; // not filled yet, skip
    const qty = parseInt(line.qtyReceived) || 0;
    const defect = parseInt(line.defectQty) || 0;
    if (qty <= 0 && defect <= 0) return null;
    const assignment = pendingAssignments.find(a => a.id === line.assignmentId);
    if (!assignment) return 'Assignment not found';
    const remaining = getRemaining(assignment);
    if (qty + defect > remaining) return `Exceeds remaining (${remaining})`;
    return null;
  };

  // Active lines = lines with assignment + qty
  const activeLines = lines.filter(l => l.assignmentId && (parseInt(l.qtyReceived) || 0) > 0);
  const hasErrors = lines.some(l => getLineError(l) !== null);
  const totalPcs = activeLines.reduce((sum, l) => sum + (parseInt(l.qtyReceived) || 0), 0);

  // Submit
  const handleSubmit = async () => {
    if (activeLines.length === 0 || hasErrors) return;
    setSubmitting(true);
    setSubmitProgress(0);

    const results = { successCount: 0, errors: [] as Array<{ label: string; error: string }> };
    const formattedDate = new Date(receiveDate).toISOString();

    for (let i = 0; i < activeLines.length; i++) {
      const line = activeLines[i];
      const assignment = pendingAssignments.find(a => a.id === line.assignmentId);
      const label = assignment ? `${assignment.design_no} · ${assignment.size} · ${assignment.worker.name}` : `#${line.assignmentId}`;

      try {
        // Receive
        await api.post(`/worker-assignments/${line.assignmentId}/receives`, {
          receive_date: formattedDate,
          quantity_received: parseInt(line.qtyReceived) || 0,
          defect_quantity: parseInt(line.defectQty) || 0,
          notes: '',
        });

        // Update rate if entered and different from assignment
        if (line.rate && assignment) {
          const newRate = parseFloat(line.rate);
          if (!isNaN(newRate) && newRate !== assignment.stitching_rate) {
            await api.patch(`/worker-assignments/${line.assignmentId}/rate`, {
              stitching_rate: newRate,
            });
          }
        }

        results.successCount++;
      } catch (err: any) {
        results.errors.push({
          label,
          error: err.response?.data?.error || err.message || 'Failed',
        });
      }

      setSubmitProgress(i + 1);
    }

    setSubmitResults(results);
    setSubmitting(false);

    if (results.errors.length === 0) {
      onSaved();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>Bulk Receive</DialogTitle>
      <DialogContent dividers>
        {/* Common date */}
        <TextField
          size="small"
          type="date"
          label="Receive Date"
          value={receiveDate}
          onChange={e => setReceiveDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2, width: isMobile ? '100%' : 200 }}
        />

        {pendingAssignments.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>No pending assignments to receive.</Alert>
        )}

        {/* Lines */}
        {lines.map((line, idx) => {
          const workerValue = workers.find(w => w.id === line.workerId) || null;
          const workerAssignments = line.workerId ? getWorkerPendingAssignments(line.workerId) : [];
          const selectedAssignment = line.assignmentId ? pendingAssignments.find(a => a.id === line.assignmentId) || null : null;
          const remaining = selectedAssignment ? getRemaining(selectedAssignment) : 0;
          const lineError = getLineError(line);

          return (
            <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Grid container spacing={1.5} alignItems="center">
                {/* Worker */}
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    size="small"
                    options={workers}
                    getOptionLabel={w => w.name}
                    value={workerValue}
                    onChange={(_, v) => handleWorkerChange(idx, v)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={params => <TextField {...params} label="Worker" placeholder="Select worker" />}
                  />
                </Grid>

                {/* Assignment */}
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    size="small"
                    options={workerAssignments}
                    getOptionLabel={getAssignmentLabel}
                    value={selectedAssignment}
                    onChange={(_, v) => handleAssignmentChange(idx, v)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    disabled={!line.workerId}
                    renderInput={params => <TextField {...params} label="Assignment" placeholder={line.workerId ? 'Select design/size' : 'Select worker first'} />}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Avatar src={option.image_url || ''} variant="rounded" sx={{ width: 36, height: 36 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{option.design_no} · {option.garment_part} · {option.size}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Issued: {option.quantity_issued} · Remaining: {getRemaining(option)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>

                {/* Rate */}
                <Grid item xs={4} sm={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="Rate ₹"
                    value={line.rate}
                    onChange={e => updateLine(idx, { rate: e.target.value })}
                    disabled={!line.assignmentId}
                    inputProps={{ min: 0, step: 0.5 }}
                  />
                </Grid>

                {/* Qty Received */}
                <Grid item xs={3} sm={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="Qty"
                    value={line.qtyReceived}
                    onChange={e => updateLine(idx, { qtyReceived: e.target.value })}
                    disabled={!line.assignmentId}
                    inputProps={{ min: 0, max: remaining }}
                    error={!!lineError}
                  />
                </Grid>

                {/* Defect Qty */}
                <Grid item xs={3} sm={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="Defects"
                    value={line.defectQty}
                    onChange={e => updateLine(idx, { defectQty: e.target.value })}
                    disabled={!line.assignmentId}
                    inputProps={{ min: 0 }}
                    error={!!lineError}
                  />
                </Grid>

                {/* Delete */}
                <Grid item xs={2} sm={0.5} sx={{ textAlign: 'center' }}>
                  {lines.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removeLine(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>

              {/* Remaining info + error */}
              {selectedAssignment && (
                <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={`Remaining: ${remaining}`} size="small" color="warning" variant="outlined" />
                  {lineError && <Typography variant="caption" color="error">{lineError}</Typography>}
                </Box>
              )}
            </Paper>
          );
        })}

        <Button size="small" startIcon={<AddIcon />} onClick={addLine} sx={{ mt: 0.5 }}>
          Add Line
        </Button>

        {/* Summary */}
        {activeLines.length > 0 && (
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Will receive: <strong>{activeLines.length} assignment(s)</strong>, <strong>{totalPcs} pcs</strong> total
            </Typography>
          </Box>
        )}

        {/* Progress */}
        {submitting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={(submitProgress / activeLines.length) * 100} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Receiving... ({submitProgress}/{activeLines.length})
            </Typography>
          </Box>
        )}

        {/* Results */}
        {submitResults && submitResults.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {submitResults.successCount} succeeded, {submitResults.errors.length} failed:
            </Typography>
            {submitResults.errors.map((e, i) => (
              <Typography key={i} variant="caption" display="block">{e.label}: {e.error}</Typography>
            ))}
            <Button size="small" sx={{ mt: 1 }} onClick={() => { onSaved(); onClose(); }}>
              Close & Refresh
            </Button>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={activeLines.length === 0 || hasErrors || submitting}
        >
          {submitting
            ? `Receiving... (${submitProgress}/${activeLines.length})`
            : `Save ${activeLines.length} Receive(s)`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
